import React, { useContext } from "react";
import { AuthContext, IntlContext } from "context";
import { HelpCircle, Info, Landmark } from "lucide-react";
import { Input } from "components/ui/input";
import { CodeCesu } from "types/cesu";
import { Link } from "react-router-dom";
import { ROUTES } from "routes";

const cesuFieldLabelClassName = "text-[11px] font-black uppercase tracking-wider text-foreground";
const cesuInputClassName =
    "h-14 w-full cursor-default rounded-[22px] border border-input bg-background px-6 text-sm text-gray-900 shadow-none focus-visible:ring-0";

const ReadOnlyCesuField: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
    <div className="space-y-2">
        <span className={cesuFieldLabelClassName}>{label}</span>
        <Input readOnly value={value ?? ""} className={cesuInputClassName} tabIndex={-1} />
    </div>
);

const ReadOnlyCesuCheckbox: React.FC<{ label: string; checked: boolean }> = ({ label, checked }) => (
    <div className="flex items-start gap-3">
        <input
            type="checkbox"
            checked={checked}
            disabled
            readOnly
            tabIndex={-1}
            className="mt-0.5 h-5 w-5 shrink-0 cursor-default rounded-md border border-input bg-background accent-primary"
        />
        <span className="text-xs leading-relaxed text-muted-foreground">{label}</span>
    </div>
);

const ClientCesuProfileSection: React.FC = () => {
    const intl = useContext(IntlContext);
    const { account } = useContext(AuthContext);
    const client = account?.resources?.client?.[0];

    if (!client) {
        return null;
    }

    const codeCesu = client.code_cesu as CodeCesu | undefined;
    const hasCesu = codeCesu === CodeCesu.HAS_CESU;
    const addCesu = codeCesu === CodeCesu.ADD_CESU;
    const showCesuDetails = hasCesu || addCesu;
    const sepaAuthorized = addCesu && Boolean(client.iban?.trim() || client.num_cesu?.trim());
    const adminAuthorized = showCesuDetails && Boolean(client.num_cesu?.trim() || addCesu);

    return (
        <div className="space-y-6">
            <p className="text-sm text-slate-500">
                Ces informations ne sont pas modifiables. Si vous devez les modifier, veuillez{" "}
                <Link to={ROUTES.CONTACT} className="font-medium text-primary hover:underline">
                    contacter nos équipes
                </Link>
                .
            </p>

            <div className="rounded-2xl border border-primary/10 bg-primary/5 p-5">
                <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-border/40">
                        <HelpCircle className="h-4 w-4" />
                    </div>
                    <div>
                        <p className="text-sm font-black text-gray-900">C&apos;est quoi le CESU ?</p>
                        <p className="mt-2 text-sm leading-relaxed text-slate-600">
                            Le Chèque Emploi Service Universel simplifie les démarches pour les services à la
                            personne. Vous restez{" "}
                            <span className="font-semibold text-primary">100% légal</span>, avec une couverture
                            santé, des cotisations retraite et une assurance en cas d&apos;accident.
                        </p>
                    </div>
                </div>
            </div>

            {codeCesu && !showCesuDetails && (
                <ReadOnlyCesuField
                    label={intl.formatMessage("register.title.cesu")}
                    value={intl.formatMessage(`register.select.code_cesu.${codeCesu}`)}
                />
            )}

            {client.num_cesu && (
                <ReadOnlyCesuField
                    label={intl.formatMessage("register.label.num_cesu")}
                    value={client.num_cesu}
                />
            )}

            {(addCesu || showCesuDetails) && (
            <div className="w-full rounded-[28px] bg-muted/30 p-6 sm:p-7">
                {addCesu && (
                    <>
                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
                                <Landmark className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="text-lg font-black">Mandat SEPA</div>
                                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                    Compte bancaire
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 space-y-5">
                            <ReadOnlyCesuField
                                label={intl.formatMessage("authentication.register.label.account_name")}
                                value={client.account_name}
                            />
                            <ReadOnlyCesuField
                                label={intl.formatMessage("authentication.register.label.iban")}
                                value={client.iban}
                            />
                            <ReadOnlyCesuField
                                label={intl.formatMessage("authentication.register.label.bic")}
                                value={client.bic}
                            />
                        </div>
                    </>
                )}

                {showCesuDetails && (
                    <div className={`space-y-4 ${addCesu ? "mt-6" : ""}`}>
                        {addCesu && (
                            <ReadOnlyCesuCheckbox
                                checked={sepaAuthorized}
                                label="J'autorise l'URSSAF à prélever automatiquement les cotisations sociales sur mon compte bancaire via un mandat SEPA."
                            />
                        )}
                        <ReadOnlyCesuCheckbox
                            checked={adminAuthorized}
                            label="J'autorise Twoghether à effectuer les démarches administratives en mon nom (déclarations auprès de l'URSSAF)."
                        />
                    </div>
                )}
            </div>
            )}

            <div className="rounded-[28px] border border-primary/10 bg-primary/5 px-6 py-5">
                <div className="flex items-center gap-3">
                    <div className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-border/40">
                        <Info className="h-4 w-4" />
                    </div>
                    <p className="min-w-0 text-sm leading-relaxed text-muted-foreground">
                        <span className="font-black text-foreground/90">Rassurez-vous :</span> Twoghether s&apos;occupe
                        de faire le lien avec l&apos;URSSAF. Vous n&apos;avez aucune démarche administrative complexe
                        à faire.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ClientCesuProfileSection;
